// ----------------------------------------------------------------
// POI layer definitions — extracted from map-view.tsx
// ----------------------------------------------------------------

export interface PoiFeature {
  id: string;
  osm_key: string;
  osm_value: string;
  name: string;
  brand: string | null;
  lat: number;
  lng: number;
}

/**
 * Build POI layer definition with an optional category filter.
 * When visibleCategories is empty or undefined, all POIs are shown.
 * When provided, only POIs matching one of the "osmKey-osmValue" keys are shown.
 * The filter is applied at the MapLibre layer level — no data re-processing needed.
 */
export function buildPoiLayerDef(visibleCategories?: Set<string>) {
  // No categories selected = hide all POIs. Categories selected = show only those.
  // undefined = show all (backward compat for consumers that don't pass this prop).
  const filter =
    visibleCategories === undefined
      ? undefined
      : visibleCategories.size === 0
        ? ['==', ['get', 'osm_key'], '__none__'] // always-false filter: hide all
        : [
            'in',
            ['concat', ['get', 'osm_key'], '-', ['get', 'osm_value']],
            ['literal', [...visibleCategories]],
          ];

  return {
    id: 'poi-layer',
    type: 'symbol' as const,
    source: 'poi-layer-source',
    minzoom: 13,
    ...(filter ? { filter } : {}),
    layout: {
      'icon-image': [
        'coalesce',
        ['image', ['concat', ['get', 'osm_key'], '-', ['get', 'osm_value']]],
        ['image', 'poi-fallback'],
      ],
      'icon-allow-overlap': false,
      'icon-ignore-placement': false,
      'icon-size': 0.35,
    },
    paint: {
      'icon-opacity': 0.6,
    },
  };
}

/** @deprecated Use buildPoiLayerDef instead */
export const poiLayerDef = buildPoiLayerDef();

export function poiFeaturesToGeoJson(pois: PoiFeature[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: pois.map((poi) => ({
      type: 'Feature',
      id: poi.id,
      geometry: {
        type: 'Point',
        coordinates: [poi.lng, poi.lat],
      },
      properties: {
        poi_id: poi.id,
        osm_key: poi.osm_key,
        osm_value: poi.osm_value,
        name: poi.name,
        brand: poi.brand,
      },
    })),
  };
}
